import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Platform, ScrollView, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  AlertTriangle,
  Camera,
  CameraOff,
  EyeOff,
  Settings,
  ShieldCheck,
  X,
} from "lucide-react-native";

import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { CompactActionButton, IconButton, ActionBanner } from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useLocale, useTranslations } from "@src/packages/i18n";
import { COACH_DISCLOSURE, disclosureClaimKey } from "../disclosure";
import { useCoachRecords } from "../records/useCoachRecords";
import {
  reduceCoachConsent,
  startCoachConsent,
  type CoachConsentEvent,
  type CoachConsentState,
} from "../consent";
import {
  needsSystemSettings,
  type MediaPermissionState,
} from "../mediaPermissions";
import { useCoachMediaPermissions } from "../hooks/useCoachMediaPermissions";
import { semanticColors } from "@louise/theme/tokens";

const ns = "care.injection_coach";

/**
 * What a patient is told, and asked, before the injection coach sees anything.
 *
 * The sheet is the consent record made visible: every sentence in it is one
 * claim from `COACH_DISCLOSURE`, in that order, and the order is the argument.
 * She is told what the coach is before she is told what it wants, and told what
 * happens to the footage before she is asked to allow any — informing after the
 * OS prompt would be informing after collection.
 *
 * Three properties are worth stating because they are easy to lose in a later
 * edit, and each has a test behind it:
 *
 * - **Refusing costs nothing.** "Without the camera" is a primary action sitting
 *   beside the other, not a link underneath it. A consent that buys access is
 *   not freely given, and #2267 requires a working no-camera mode regardless.
 * - **Nothing is kept.** Not a setting, not a default — there is no code path in
 *   the coach that could keep or send a frame. `no-retention.test.ts` reads the
 *   source and fails if one appears.
 * - **Stopping is one tap.** Dismissing the sheet withdraws; the machine treats
 *   `withdraw` before anything else so no state can swallow it.
 */
export function InjectionCoachConsentSheet({
  visible,
  medicationName,
  onCancel,
  onReady,
  cameraRole = "guide",
}: {
  visible: boolean;
  /** The name on her box, so the sheet names the product she is holding. */
  medicationName: string;
  onCancel: () => void;
  /** Coaching may begin. `withCamera` reflects what she actually allowed. */
  onReady: (withCamera: boolean) => void;
  /**
   * What her camera would do here, which changes what she is being offered.
   *
   * A guide outlines the injection area on her body; a mirror lets her see a
   * site she cannot look at. Offering "turn on the camera guide" for an
   * intramuscular dose would promise a placement the geometry cannot make, so
   * the choice says what she will actually get. The eight claims are unchanged
   * — nothing about what is kept, sent or recorded differs between the two —
   * so the disclosure keeps its version.
   */
  cameraRole?: "guide" | "mirror";
}) {
  const t = useTranslations();
  const insets = useSafeAreaInsets();
  const { permissions, refresh, requestCamera, openSystemSettings } =
    useCoachMediaPermissions();
  const [consent, setConsent] = useState<CoachConsentState>(startCoachConsent);
  /**
   * The same state, readable synchronously.
   *
   * The machine's answer decides whether coaching may begin, and that answer has
   * to be read right after the event is applied — while the OS permission dialog
   * was up, she may have dismissed the sheet, and the reducer will have moved to
   * `withdrawn`. Reading it out of a `setState` updater worked only through
   * React's eager-state bailout, which is an optimisation and not a contract:
   * when it does not fire, the updater has not run yet and the answer is still
   * null, so `onReady` never fires and the coach does not start after she
   * granted the camera.
   *
   * A ref alongside the state lets the reduction happen in plain code, where its
   * result is simply the value it returns. It also keeps the updater pure, which
   * the rest of this file argues for at length.
   */
  const consentRef = useRef(consent);

  /** Apply one event, and hand back what the machine decided. */
  const applyConsent = useCallback(
    (event: CoachConsentEvent): CoachConsentState => {
      const next = reduceCoachConsent(consentRef.current, event);
      consentRef.current = next;
      setConsent(next);
      return next;
    },
    [],
  );

  // Read from the machine rather than a parallel boolean. The OS prompt is open
  // exactly while the machine is in `requesting-camera`, and a second flag
  // tracking the same fact is a second thing that can disagree with it.
  const asking = consent.status === "requesting-camera";

  // A fresh disclosure every time the sheet opens. Nothing durable remembers a
  // previous answer — there is nowhere to record one that would be demonstrable
  // — so nothing pretends to, and her consent stays contemporaneous with the
  // injection it covers.
  useEffect(() => {
    if (!visible) return;
    const fresh = startCoachConsent();
    consentRef.current = fresh;
    setConsent(fresh);
    void refresh();
  }, [visible, refresh]);

  const withdraw = useCallback(() => {
    applyConsent({ type: "withdraw" });
    onCancel();
  }, [applyConsent, onCancel]);

  /**
   * A refusal she has not been told about yet.
   *
   * The walk used to start camera-less and say nothing, so a patient who chose
   * "continue with my camera" and then missed the browser's prompt — or tapped
   * the wrong button on it — simply never got a picture, with no sentence
   * anywhere explaining where it went. Holding the sheet open is the only
   * moment she can still do something about it.
   */
  const [refusal, setRefusal] = useState<MediaPermissionState | null>(null);

  /**
   * The receipt, written when she accepts.
   *
   * The text sent is assembled from the same claims, in the same order, through
   * the same translator that renders them above — so what is hashed is what was
   * on her screen rather than a paraphrase of it. Assembled at the moment she
   * accepts, not at render, because a locale changed between the two would
   * otherwise record the wrong words.
   */
  const { recordConsent } = useCoachRecords();
  const { locale } = useLocale();
  const acceptedText = useCallback(
    () =>
      COACH_DISCLOSURE.claims
        .map((claim) => t(disclosureClaimKey(claim)))
        .join("\n"),
    [t],
  );
  const writeReceipt = useCallback(() => {
    // Not awaited, on purpose: a receipt is never a reason to keep her waiting
    // in front of a needle, and nothing on the next screen depends on it.
    void recordConsent(
      COACH_DISCLOSURE,
      locale === "fr" ? "fr" : "en",
      acceptedText(),
    );
  }, [acceptedText, locale, recordConsent]);

  const continueWithCamera = useCallback(async () => {
    // Asking again re-acknowledges the disclosure from the top rather than
    // pushing the machine backwards: `coaching` is not a state you leave, and a
    // second consent to the same sheet is what a second attempt is.
    if (consentRef.current.status === "coaching") {
      const fresh = startCoachConsent();
      consentRef.current = fresh;
      setConsent(fresh);
    }
    setRefusal(null);
    applyConsent({ type: "acknowledge-with-camera" });
    const permission = await requestCamera();
    // Read the machine's answer rather than assuming it. If she dismissed the
    // sheet while the OS dialog was up, the reducer has already moved to
    // `withdrawn` and refuses to advance — reporting `onReady` anyway would
    // start a session on a consent she had just taken back. The reducer is only
    // a guarantee if its callers honour what it returns.
    const settled = applyConsent({
      type: "camera-permission-settled",
      permission,
    });
    if (settled.status !== "coaching") return;
    if (permission !== "granted") {
      // A refusal is not an error state and must never be a dead end — but it
      // must not be silent either. She is told what happened and keeps both
      // doors: allow it and ask again, or continue without the camera, which
      // is the button already under this one.
      setRefusal(permission);
      return;
    }
    writeReceipt();
    onReady(true);
  }, [applyConsent, requestCamera, onReady, writeReceipt]);

  const continueWithoutCamera = useCallback(() => {
    // Guarded like the camera answer beside it, which is disabled while the OS
    // prompt is up. This one answers instantly, so two taps in the time it
    // takes the sheet to unmount wrote two receipts for one acceptance.
    if (consentRef.current.status === "coaching") return;
    setRefusal(null);
    applyConsent({ type: "acknowledge-without-camera" });
    // Recorded too. Coaching without the camera is still coaching under this
    // disclosure, and a receipt that only existed for one of the two answers
    // would say she consented only when she said yes to a camera.
    writeReceipt();
    onReady(false);
  }, [applyConsent, onReady, writeReceipt]);

  const cameraBlocked = needsSystemSettings(permissions.camera);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={withdraw}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: semanticColors.overlay.scrim,
          justifyContent: "flex-end",
        }}
      >
        <View
          style={{
            backgroundColor: semanticColors.surface.sheet,
            borderTopLeftRadius: 26,
            borderTopRightRadius: 26,
            paddingTop: 18,
            // Enough that the second action clears the edge on a browser, where
            // there is no safe-area inset to lean on.
            paddingBottom: Math.max(insets.bottom, 24) + 12,
            maxHeight: "88%",
            // The app is a phone layout that also runs in a desktop browser. A
            // bottom sheet left unconstrained spans 1900px there, which turns a
            // paragraph into a single line of very small text.
            width: "100%",
            maxWidth: 520,
            alignSelf: "center",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 12,
              paddingHorizontal: 20,
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                className="font-instrument-serif"
                style={{
                  fontSize: 23,
                  lineHeight: 28,
                  color: EMBER.colors.text,
                }}
              >
                {t(`${ns}.before_we_start`)}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13.5,
                  lineHeight: 20,
                  color: EMBER.colors.textMid,
                  marginTop: 6,
                }}
              >
                {t(`${ns}.coaching_for_medication`, {
                  params: { medication: medicationName },
                })}
              </Text>
            </View>
            <IconButton
              accessibilityLabel={t(`${ns}.not_now`)}
              action={`${ns}.not_now`}
              onPress={withdraw}
              hitSlop={10}
              size="sm"
              surface="bare"
            >
              <X size={20} color={EMBER.colors.textLight} />
            </IconButton>
          </View>

          <ScrollView
            style={{ marginTop: 14 }}
            contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 8 }}
          >
            <View style={{ gap: 12 }}>
              {COACH_DISCLOSURE.claims.map((claim) => (
                <View
                  key={claim}
                  style={{
                    flexDirection: "row",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <ClaimIcon claim={claim} />
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      fontSize: 14,
                      lineHeight: 20,
                      color: EMBER.colors.text,
                    }}
                  >
                    {t(disclosureClaimKey(claim))}
                  </Text>
                </View>
              ))}
            </View>

            {cameraBlocked ? (
              // Said plainly rather than letting her pick "with the camera" and
              // watch nothing happen: once blocked, the OS prompt is a no-op and
              // only Settings can change the answer.
              <ActionBanner
                variant="warning"
                message={t(`${ns}.camera_is_turned_off_in_system_settings`)}
                actionLabel={t(`${ns}.open_system_settings`)}
                action="injection_coach_open_settings"
                actionIcon={Settings}
                onAction={openSystemSettings}
                style={{ marginTop: 16 }}
              />
            ) : refusal ? (
              <ActionBanner
                variant="warning"
                message={t(
                  Platform.OS === "web"
                    ? `${ns}.allow_camera_access_in_your_browser`
                    : `${ns}.allow_camera_access_and_ask_again`,
                )}
                style={{ marginTop: 16 }}
              />
            ) : null}

            {/* What a mirror is, said where the mirror is offered.
                Deliberately outside the eight claims above: those are the
                disclosure, they are hashed into her receipt, and they say the
                same thing whichever procedure this is. This is the one
                sentence that differs, and it explains why the button below
                says something else than usual. */}
            {cameraRole === "mirror" ? (
              <Text
                className="font-dm-sans"
                style={{
                  marginTop: 16,
                  fontSize: 13,
                  lineHeight: 19,
                  color: EMBER.colors.textMid,
                }}
              >
                {t(`${ns}.the_screen_can_show_you_a_site_you_cannot_see`)}
              </Text>
            ) : null}

            <Text
              className="font-dm-sans"
              style={{
                marginTop: 16,
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textLight,
              }}
            >
              {t(`${ns}.this_choice_lasts_only_for_this_session`)}
            </Text>
          </ScrollView>

          <View style={{ paddingHorizontal: 20, paddingTop: 12, gap: 10 }}>
            {/* Both are primary, side by side in weight. Putting "without" in a
                text link underneath would make the camera the default and the
                consent the price of entry. */}
            <CompactActionButton
              variant="primary"
              action="injection_coach_continue_with_camera"
              label={t(
                cameraRole === "mirror"
                  ? `${ns}.use_the_camera_as_a_mirror`
                  : `${ns}.continue_with_my_camera`,
              )}
              leadingIcon={Camera}
              iconSize={16}
              loading={asking}
              disabled={asking || cameraBlocked}
              onPress={() => void continueWithCamera()}
            />
            <CompactActionButton
              variant="secondary"
              action="injection_coach_continue_without_camera"
              label={t(`${ns}.continue_without_my_camera`)}
              icon={<CameraOff size={16} color={EMBER.colors.text} />}
              disabled={asking}
              onPress={continueWithoutCamera}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

function ClaimIcon({
  claim,
}: {
  claim: (typeof COACH_DISCLOSURE.claims)[number];
}) {
  const size = 16;
  if (claim === "nothing-is-recorded-or-saved") {
    return <EyeOff size={size} color={EMBER.colors.warm} />;
  }
  if (claim === "nothing-is-shared-with-your-clinic") {
    return <ShieldCheck size={size} color={EMBER.colors.warm} />;
  }
  if (claim === "not-for-emergencies-call-your-clinic-or-emergency-services") {
    return <AlertTriangle size={size} color={semanticColors.status.danger} />;
  }
  if (claim === "you-can-turn-the-camera-off-at-any-moment") {
    return <CameraOff size={size} color={EMBER.colors.textMid} />;
  }
  return <Camera size={size} color={EMBER.colors.textMid} />;
}
